import { useState, useEffect } from 'react';
import axios from 'axios';
import { AdminLayout, PageHeader } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Search, Shield, ShieldOff, Users, Calendar, Building2,
  Plus, Mail, Loader2, X, UserPlus, Trash2, KeyRound, Pencil, Save, ShieldCheck
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_USER = { email: '', contact_name: '', company_name: '', phone: '', password: '' };
const EMPTY_INVITE = { email: '', contact_name: '', company_name: '', phone: '' };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [toggleUser, setToggleUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Add User modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ ...EMPTY_USER });
  const [addSaving, setAddSaving] = useState(false);

  // Invite modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ ...EMPTY_INVITE });
  const [inviteSaving, setInviteSaving] = useState(false);

  // Reset Password modal
  const [resetUser, setResetUser] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetSaving, setResetSaving] = useState(false);

  // Edit User modal
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ email: '', contact_name: '', company_name: '' });
  const [editSaving, setEditSaving] = useState(false);

  // Reset MFA
  const [resetMfaUser, setResetMfaUser] = useState(null);
  const [resetMfaSaving, setResetMfaSaving] = useState(false);

  useEffect(() => { loadUsers(); }, []);

  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      setFilteredUsers(users.filter(u =>
        u.email?.toLowerCase().includes(term) ||
        u.contact_name?.toLowerCase().includes(term) ||
        u.company_name?.toLowerCase().includes(term)
      ));
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAdmin = async () => {
    if (!toggleUser) return;
    try {
      await axios.put(`${API_URL}/admin/users/${toggleUser.id}`, { is_admin: !toggleUser.is_admin });
      setUsers(users.map(u => u.id === toggleUser.id ? { ...u, is_admin: !u.is_admin } : u));
      toast.success(`${toggleUser.contact_name} is now ${toggleUser.is_admin ? 'a regular user' : 'an admin'}`);
    } catch (error) {
      toast.error('Failed to update user');
    } finally {
      setToggleUser(null);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUser) return;
    setDeleting(true);
    try {
      const res = await axios.delete(`${API_URL}/admin/users/${deleteUser.id}`);
      toast.success(res.data.message);
      setUsers(users.filter(u => u.id !== deleteUser.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
      setDeleteUser(null);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setResetSaving(true);
    try {
      await axios.post(`${API_URL}/admin/users/${resetUser.id}/reset-password`, { new_password: resetPassword });
      toast.success(`Password reset for ${resetUser.contact_name}`);
      setResetUser(null);
      setResetPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetSaving(false);
    }
  };

  const openEditUser = (user) => {
    setEditForm({ email: user.email, contact_name: user.contact_name, company_name: user.company_name || '' });
    setEditUser(user);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      const payload = {};
      if (editForm.email !== editUser.email) payload.email = editForm.email;
      if (editForm.contact_name !== editUser.contact_name) payload.contact_name = editForm.contact_name;
      if (editForm.company_name !== editUser.company_name) payload.company_name = editForm.company_name;

      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save');
        setEditUser(null);
        return;
      }
      await axios.put(`${API_URL}/admin/users/${editUser.id}/details`, payload);
      toast.success('User updated successfully');
      setEditUser(null);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setEditSaving(false);
    }
  };

  const handleResetMfa = async () => {
    if (!resetMfaUser) return;
    setResetMfaSaving(true);
    try {
      const res = await axios.post(`${API_URL}/admin/users/${resetMfaUser.id}/reset-mfa`);
      toast.success(res.data.message);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset MFA');
    } finally {
      setResetMfaSaving(false);
      setResetMfaUser(null);
    }
  };


  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!addForm.email || !addForm.contact_name || !addForm.company_name || !addForm.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (addForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setAddSaving(true);
    try {
      await axios.post(`${API_URL}/admin/users`, addForm);
      toast.success(`User ${addForm.contact_name} created successfully`);
      setShowAddModal(false);
      setAddForm({ ...EMPTY_USER });
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setAddSaving(false);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    if (!inviteForm.email || !inviteForm.contact_name || !inviteForm.company_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    setInviteSaving(true);
    try {
      await axios.post(`${API_URL}/admin/users/invite`, inviteForm);
      toast.success(`Invitation sent to ${inviteForm.email}`);
      setShowInviteModal(false);
      setInviteForm({ ...EMPTY_INVITE });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviteSaving(false);
    }
  };

  const adminCount = users.filter(u => u.is_admin).length;
  const clientCount = users.filter(u => !u.is_admin).length;

  return (
    <AdminLayout>
      <div className="p-8">
        <PageHeader
          title="User Management"
          description="Manage client accounts, passwords, and admin access."
          actions={
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setShowInviteModal(true)} data-testid="invite-user-btn">
                <Mail className="h-4 w-4 mr-2" />Invite User
              </Button>
              <Button className="btn-primary" onClick={() => setShowAddModal(true)} data-testid="add-user-btn">
                <Plus className="h-4 w-4 mr-2" />Add User
              </Button>
            </div>
          }
        />

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bento-card" data-testid="stat-total-users">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-[#FF6B35]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{users.length}</p>
                <p className="text-sm text-slate-500">Total Users</p>
              </div>
            </div>
          </div>
          <div className="bento-card" data-testid="stat-admins">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Shield className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{adminCount}</p>
                <p className="text-sm text-slate-500">Admins</p>
              </div>
            </div>
          </div>
          <div className="bento-card" data-testid="stat-clients">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Building2 className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{clientCount}</p>
                <p className="text-sm text-slate-500">Clients</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name, email, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="user-search"
          />
        </div>

        {/* User Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full" data-testid="users-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
                <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Company</th>
                <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Joined</th>
                <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</th>
                <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-slate-500">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />Loading...
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr><td colSpan="5" className="p-8 text-center text-slate-500">No users found</td></tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`user-row-${user.id}`}>
                    <td className="p-4">
                      <div>
                        <p className="font-medium text-slate-900">{user.contact_name}</p>
                        <p className="text-sm text-slate-500">{user.email}</p>
                      </div>
                    </td>
                    <td className="p-4 text-slate-700">{user.company_name || '-'}</td>
                    <td className="p-4 text-slate-500 text-sm">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {new Date(user.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        user.is_admin
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {user.is_admin ? <><Shield className="h-3 w-3" /> Admin</> : 'Client'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1 flex-wrap">
                        <Button size="sm" variant="ghost" onClick={() => openEditUser(user)} className="text-slate-600 hover:text-slate-800" data-testid={`edit-user-${user.id}`}>
                          <Pencil className="h-3.5 w-3.5 mr-1" />Edit
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setResetUser(user); setResetPassword(''); }} className="text-blue-600 hover:text-blue-700" data-testid={`reset-pw-${user.id}`}>
                          <KeyRound className="h-3.5 w-3.5 mr-1" />Reset PW
                        </Button>
                        {user.mfa_enabled && (
                          <Button size="sm" variant="ghost" onClick={() => setResetMfaUser(user)} className="text-purple-600 hover:text-purple-700" data-testid={`reset-mfa-${user.id}`}>
                            <ShieldCheck className="h-3.5 w-3.5 mr-1" />Reset MFA
                          </Button>
                        )}
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => setToggleUser(user)}
                          className={user.is_admin ? 'text-amber-600 hover:text-amber-700' : 'text-emerald-600 hover:text-emerald-700'}
                          data-testid={`toggle-admin-${user.id}`}
                        >
                          {user.is_admin ? <><ShieldOff className="h-3.5 w-3.5 mr-1" />Revoke</> : <><Shield className="h-3.5 w-3.5 mr-1" />Admin</>}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setDeleteUser(user)} className="text-red-500 hover:text-red-700" data-testid={`delete-user-${user.id}`}>
                          <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Toggle Admin Dialog */}
        <AlertDialog open={!!toggleUser} onOpenChange={() => setToggleUser(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{toggleUser?.is_admin ? 'Remove Admin Access?' : 'Grant Admin Access?'}</AlertDialogTitle>
              <AlertDialogDescription>
                {toggleUser?.is_admin
                  ? `${toggleUser?.contact_name} will lose admin privileges and can only access the client portal.`
                  : `${toggleUser?.contact_name} will gain full admin access including user management and settings.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleToggleAdmin} data-testid="confirm-toggle-admin">Confirm</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete User Dialog */}
        <AlertDialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete User?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete <strong>{deleteUser?.contact_name}</strong> ({deleteUser?.email}) and all their submissions. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700" disabled={deleting} data-testid="confirm-delete-user">
                {deleting ? 'Deleting...' : 'Delete User'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reset MFA Dialog */}
        <AlertDialog open={!!resetMfaUser} onOpenChange={() => setResetMfaUser(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset MFA for {resetMfaUser?.contact_name}?</AlertDialogTitle>
              <AlertDialogDescription>
                This will disable two-factor authentication for <strong>{resetMfaUser?.contact_name}</strong> ({resetMfaUser?.email}). They will need to set up MFA again on their next login or from their profile.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleResetMfa} className="bg-purple-600 hover:bg-purple-700" disabled={resetMfaSaving} data-testid="confirm-reset-mfa">
                {resetMfaSaving ? 'Resetting...' : 'Reset MFA'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reset Password Modal */}
        {resetUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black/50" onClick={() => setResetUser(null)} />
            <div className="relative bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4 z-10" data-testid="reset-pw-modal">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                    <KeyRound className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Reset Password</h2>
                    <p className="text-xs text-slate-500">{resetUser.contact_name} — {resetUser.email}</p>
                  </div>
                </div>
                <button onClick={() => setResetUser(null)} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-1.5">
                  <Label>New Password *</Label>
                  <Input type="password" value={resetPassword} onChange={e => setResetPassword(e.target.value)} placeholder="Min 8 characters" required data-testid="reset-pw-input" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setResetUser(null)}>Cancel</Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={resetSaving} data-testid="reset-pw-submit">
                    {resetSaving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Resetting...</> : <><KeyRound className="h-4 w-4 mr-2" />Reset Password</>}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {editUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black/50" onClick={() => setEditUser(null)} />
            <div className="relative bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4 z-10" data-testid="edit-user-modal">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center">
                    <Pencil className="h-5 w-5 text-[#FF6B35]" />
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Edit User</h2>
                </div>
                <button onClick={() => setEditUser(null)} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={handleEditUser} className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <Input type="email" value={editForm.email} onChange={e => setEditForm(f => ({...f, email: e.target.value}))} data-testid="edit-user-email" />
                </div>
                <div className="space-y-1.5">
                  <Label>Contact Name</Label>
                  <Input value={editForm.contact_name} onChange={e => setEditForm(f => ({...f, contact_name: e.target.value}))} data-testid="edit-user-name" />
                </div>
                <div className="space-y-1.5">
                  <Label>Company Name</Label>
                  <Input value={editForm.company_name} onChange={e => setEditForm(f => ({...f, company_name: e.target.value}))} data-testid="edit-user-company" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setEditUser(null)}>Cancel</Button>
                  <Button type="submit" className="btn-primary" disabled={editSaving} data-testid="edit-user-submit">
                    {editSaving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</> : <><Save className="h-4 w-4 mr-2" />Save Changes</>}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add User Modal */}
        {showAddModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowAddModal(false)} />
            <div className="relative bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4 z-10" data-testid="add-user-modal">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center">
                    <UserPlus className="h-5 w-5 text-[#FF6B35]" />
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Add New User</h2>
                </div>
                <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={handleAddUser} className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Contact Name *</Label>
                  <Input value={addForm.contact_name} onChange={e => setAddForm(f => ({...f, contact_name: e.target.value}))} placeholder="John Smith" data-testid="add-user-name" required />
                </div>
                <div className="space-y-1.5">
                  <Label>Email *</Label>
                  <Input type="email" value={addForm.email} onChange={e => setAddForm(f => ({...f, email: e.target.value}))} placeholder="john@company.com" data-testid="add-user-email" required />
                </div>
                <div className="space-y-1.5">
                  <Label>Company Name *</Label>
                  <Input value={addForm.company_name} onChange={e => setAddForm(f => ({...f, company_name: e.target.value}))} placeholder="Acme Corp" data-testid="add-user-company" required />
                </div>
                <div className="space-y-1.5">
                  <Label>Phone</Label>
                  <Input value={addForm.phone} onChange={e => setAddForm(f => ({...f, phone: e.target.value}))} placeholder="(555) 123-4567" data-testid="add-user-phone" />
                </div>
                <div className="space-y-1.5">
                  <Label>Temporary Password *</Label>
                  <Input type="password" value={addForm.password} onChange={e => setAddForm(f => ({...f, password: e.target.value}))} placeholder="Min 8 characters" data-testid="add-user-password" required />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
                  <Button type="submit" className="btn-primary" disabled={addSaving} data-testid="add-user-submit">
                    {addSaving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Creating...</> : <><Plus className="h-4 w-4 mr-2" />Create User</>}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Invite User Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowInviteModal(false)} />
            <div className="relative bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4 z-10" data-testid="invite-user-modal">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                    <Mail className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Invite User</h2>
                    <p className="text-xs text-slate-500">They'll set their own password via email link</p>
                  </div>
                </div>
                <button onClick={() => setShowInviteModal(false)} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={handleInviteUser} className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Contact Name *</Label>
                  <Input value={inviteForm.contact_name} onChange={e => setInviteForm(f => ({...f, contact_name: e.target.value}))} placeholder="Jane Doe" data-testid="invite-user-name" required />
                </div>
                <div className="space-y-1.5">
                  <Label>Email *</Label>
                  <Input type="email" value={inviteForm.email} onChange={e => setInviteForm(f => ({...f, email: e.target.value}))} placeholder="jane@company.com" data-testid="invite-user-email" required />
                </div>
                <div className="space-y-1.5">
                  <Label>Company Name *</Label>
                  <Input value={inviteForm.company_name} onChange={e => setInviteForm(f => ({...f, company_name: e.target.value}))} placeholder="Acme Corp" data-testid="invite-user-company" required />
                </div>
                <div className="space-y-1.5">
                  <Label>Phone</Label>
                  <Input value={inviteForm.phone} onChange={e => setInviteForm(f => ({...f, phone: e.target.value}))} placeholder="(555) 123-4567" data-testid="invite-user-phone" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowInviteModal(false)}>Cancel</Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={inviteSaving} data-testid="invite-user-submit">
                    {inviteSaving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Sending...</> : <><Mail className="h-4 w-4 mr-2" />Send Invitation</>}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
